<!-- Energy widget version: 1.0 -->
<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, ProgressBar, Text } from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores';
	import {
		consumption_by_device_account,
		consumption_device,
		consumption_rooms,
	} from 'src/stores/features/consumption';
	import { rooms } from 'src/stores';
	import EmInfoTooltip from 'src/components/EnergyMonitoring/EmInfoTooltip.svelte';
	import { buildConsumptionStores } from 'src/components/EnergyMonitoring/helpers/consumption_helpers';
	import { buildLivePowerPair } from 'src/components/EnergyMonitoring/helpers/energy_kpi_helpers';
	import type { DashEnergyWidgetConfig } from '../../../../dash-energy-types';
	import { normalizeEnergyConfig } from '../../../../dash-energy-helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';

	export let energyConfig: DashEnergyWidgetConfig<'dashEnergyKpiLive'> | undefined;
	export let staticPreview = false;

	$: config = normalizeEnergyConfig('dashEnergyKpiLive', energyConfig);

	$: consumptionStores = buildConsumptionStores({
		rooms: $rooms,
		devices: $devices,
		consumptionRooms: $consumption_rooms,
		consumptionDevice: $consumption_device,
		consumptionByDeviceAccount: $consumption_by_device_account,
	});

	$: pair = buildLivePowerPair(
		consumptionStores,
		$devices,
		staticPreview || $energyMonitoringDataMode === 'demo',
	);
</script>

<Container type="glass" noScroll fullHeight>
	<div class="dash-energy-kpi-live">
		<div class="dash-energy-kpi-values">
			<div class="dash-energy-kpi-value-block">
				<Text styleType="miniature" class="text-color-muted">
					{$format('energy_monitoring_kpi_load', { default: 'Live load' })}
				</Text>
				<Text styleType="h2" class="text-600">{pair.loadValue}</Text>
			</div>
			<div class="dash-energy-kpi-value-block dash-energy-kpi-value-right">
				<Text styleType="miniature" class="text-color-muted">
					{$format('energy_monitoring_kpi_solar', { default: 'Solar roof' })}
				</Text>
				<div class="dash-energy-kpi-value-heading">
					<Text styleType="h2" class="text-600">{pair.solarValue}</Text>
					<EmInfoTooltip
						text={$format('energy_monitoring_kpi_solar_info', {
							default: 'Power your solar setup is generating at this moment.',
						})}
					/>
				</div>
			</div>
		</div>
		<div class="dash-energy-kpi-bar">
			<Text styleType="miniature" class="text-color-muted">
				{pair.selfPoweredPercent > 0
					? $format('energy_monitoring_self_powered_percent', {
							values: { percent: pair.selfPoweredPercent },
							default: `${pair.selfPoweredPercent}% self-powered`,
						})
					: pair.gridLabel || pair.loadLabel}
			</Text>
			<ProgressBar
				progress={pair.selfPoweredPercent > 0 ? pair.selfPoweredPercent : 0}
				type={pair.selfPoweredPercent >= 50 ? 'success' : pair.progressType}
			/>
		</div>
	</div>
</Container>

<style lang="scss">
	.dash-energy-kpi-live {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		min-width: 0;
		padding: var(--spacing-s);
	}

	.dash-energy-kpi-values {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--spacing-m);
	}

	.dash-energy-kpi-value-block {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}

	.dash-energy-kpi-value-right {
		align-items: flex-end;
		text-align: right;
	}

	.dash-energy-kpi-value-heading {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xxs);
	}

	.dash-energy-kpi-bar {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}
</style>
